import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

interface ChartData {
  name: string;
  value: number;
}

interface AnalyticsChartProps {
  chartdata?: ChartData[];
  formatValue?: (value: number) => string;
  height?: number;
  className?: string;
  chartType?: string;
}

const AnalyticsChart: React.FC<AnalyticsChartProps> = ({
  chartdata,
  chartType,
  formatValue,
  height = 400,
  className = "",
}) => {
  const defaultFormatValue = (value: number): string => {
    if (formatValue) return formatValue(value);

    const formatted = new Intl.NumberFormat("en-NG", {
      style:
        chartType === "payment" || chartType === "transaction"
          ? "currency"
          : "decimal",
      minimumFractionDigits:
        chartType === "payment" || chartType === "transaction" ? 2 : 0,
      maximumFractionDigits:
        chartType === "payment" || chartType === "transaction" ? 2 : 0,
      currency: "NGN",
    }).format(value);

    return `${formatted}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-[#0979A1]">
              {defaultFormatValue(payload[0].value)}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomYAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={4}
          textAnchor="end"
          fill="#6B7280"
          fontSize="12"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {defaultFormatValue(payload.value)}
        </text>
      </g>
    );
  };

  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fill="#6B7280"
          fontSize="12"
          fontFamily="system-ui, -apple-system, sans-serif"
        >
          {payload.value}
        </text>
      </g>
    );
  };

  return (
    <div className={`w-full bg-white p-6 ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartdata}
          margin={{
            top: 20,
            right: 30,
            left: 80,
            bottom: 20,
          }}
          barCategoryGap="20%"
        >
          <defs>
            <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#CFEEF9" stopOpacity={1} />
              <stop offset="100%" stopColor="#0979A1" stopOpacity={1} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={<CustomXAxisTick />}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={<CustomYAxisTick />}
            domain={[0, "dataMax"]}
            tickCount={6}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(9, 121, 161, 0.1)" }}
          />
          <Bar dataKey="value" fill="url(#barGradient)" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default AnalyticsChart;
